// React error boundary.
//
// Used twice — once at the root (main.tsx) for catastrophic errors, once around
// the Layout <Outlet> (resetKey={pathname}) so a crashing tab shows a fallback
// with the nav intact and navigating away clears it. Also catches lazy-chunk
// load failures from the route-level code splitting and offers a reload to
// fetch the new bundle.

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** When this value changes, a tripped boundary clears itself (e.g. route path). */
  resetKey?: unknown;
  /** Custom fallback; defaults to DefaultFallback. */
  fallback?: (err: Error, reset: () => void) => ReactNode;
}
interface State {
  err: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { err: null };

  static getDerivedStateFromError(err: Error): State {
    return { err };
  }

  override componentDidUpdate(prev: Props) {
    // Navigating to a different route (resetKey change) clears a tripped tab.
    if (this.state.err && prev.resetKey !== this.props.resetKey) {
      this.setState({ err: null });
    }
  }

  override componentDidCatch(err: Error, info: ErrorInfo) {
    console.error('react_error_boundary', {
      error: err.message,
      stack: err.stack?.slice(0, 1500),
      componentStack: info.componentStack?.slice(0, 1500),
    });
  }

  private reset = () => this.setState({ err: null });

  override render() {
    if (this.state.err) {
      return this.props.fallback
        ? this.props.fallback(this.state.err, this.reset)
        : <DefaultFallback err={this.state.err} reset={this.reset} />;
    }
    return this.props.children;
  }
}

function isChunkLoadError(err: Error): boolean {
  return /loading chunk|dynamically imported module|failed to fetch dynamically|importing a module script failed/i
    .test(`${err.name} ${err.message}`);
}

function DefaultFallback({ err, reset }: { err: Error; reset: () => void }) {
  const chunk = isChunkLoadError(err);
  return (
    <div className="border border-red-200 bg-red-50 rounded-xl p-6 max-w-2xl mx-auto my-8 space-y-3">
      <h2 className="text-lg font-semibold text-red-800">
        {chunk ? 'A newer version is available' : 'Something went wrong'}
      </h2>
      <p className="text-sm text-red-700">
        {chunk
          ? 'This part of the app failed to load, usually because it was updated while your tab was open. Reload to get the latest version.'
          : 'This view hit an unexpected error. The rest of the app is still fine — you can retry, or head back to the feed.'}
      </p>
      <pre className="text-xs text-red-600/80 bg-white/60 rounded p-2 overflow-x-auto whitespace-pre-wrap break-words">
        {err.message}
      </pre>
      <div className="flex gap-2 pt-1">
        {chunk ? (
          <button
            onClick={() => window.location.reload()}
            className="px-3 py-1.5 text-sm bg-red-700 text-white rounded hover:bg-red-800"
          >
            Reload
          </button>
        ) : (
          <>
            <button
              onClick={reset}
              className="px-3 py-1.5 text-sm bg-red-700 text-white rounded hover:bg-red-800"
            >
              Try again
            </button>
            <a
              href="/opportunities"
              className="px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded hover:bg-red-100"
            >
              Back to feed
            </a>
          </>
        )}
      </div>
    </div>
  );
}
